import type React from 'react';
import type { ComponentProps } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  Completion,
  CompletionContext,
  CompletionResult,
  CompletionSource,
} from '@codemirror/autocomplete';
import {
  autocompletion,
  completeFromList,
  completionKeymap,
} from '@codemirror/autocomplete';
import { defaultKeymap, indentWithTab } from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import { python } from '@codemirror/lang-python';
import { yaml } from '@codemirror/lang-yaml';
import type { Diagnostic } from '@codemirror/lint';
import { lintGutter, setDiagnostics } from '@codemirror/lint';
import { keymap } from '@codemirror/view';
import type { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import ReactCodeMirror, { lineNumbers } from '@uiw/react-codemirror';
import cn from 'classnames';
import { useTheme } from 'styled-components';

import { IconButton } from 'components/atoms/IconButton';
import { CopyIcon } from 'components/atoms/Icons';
import { KeyboardSafeTextArea } from 'components/atoms/KeyboardSafeInputs';
import Tooltip from 'components/atoms/Tooltip';
import * as Styled from './CodeInput.styled';
export interface CodeInputProps {
  value?: string;
  defaultValue?: string;
  className?: string;
  name?: string;
  errors?: string[];
  diagnostics?: Diagnostic[];
  extraExtensions?: ComponentProps<typeof ReactCodeMirror>['extensions'];
  onChange?: (value: string) => void;
  onBlur?: React.DOMAttributes<HTMLInputElement>['onBlur'];
  readOnly?: boolean;
  placeholder?: string;
  completions?: Completion[];
  language?: 'python' | 'json' | 'yaml';
  dataTestId?: string;
  lineWrapping?: boolean;
  showCopyButton?: boolean;
  viewRef?: React.MutableRefObject<ReactCodeMirrorRef | null>;
  skipLines?: number;
  showLineNumbers?: boolean;
}

// workaround to dispatch event on controlled inputs
// see https://github.com/facebook/react/issues/10135#issuecomment-314441175
function setNativeValue(element: HTMLTextAreaElement | null, value: string) {
  const valueSetter = Object?.getOwnPropertyDescriptor(element, 'value')?.set;
  const prototype = Object.getPrototypeOf(element);
  const prototypeValueSetter = Object?.getOwnPropertyDescriptor(
    prototype,
    'value',
  )?.set;

  if (valueSetter && valueSetter !== prototypeValueSetter) {
    prototypeValueSetter?.call(element, value);
  } else {
    valueSetter?.call(element, value);
  }
}

export const CodeInput: React.FC<CodeInputProps> = ({
  value,
  defaultValue,
  className,
  name,
  errors = [],
  diagnostics,
  extraExtensions = [],
  onBlur,
  onChange,
  readOnly = false,
  placeholder = '',
  completions = [],
  language,
  lineWrapping = false,
  dataTestId,
  showCopyButton = false,
  viewRef = null,
  skipLines: skipLines,
  showLineNumbers = true,
}) => {
  const theme = useTheme() as any;
  const isDarkMode = theme?.activeColorScheme?.type === 'dark';
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const innerViewRef = useRef<ReactCodeMirrorRef>(null);
  const view = useMemo(() => viewRef || innerViewRef, [viewRef]);

  const [innerValue, setInnerValue] = useState(value ?? defaultValue);

  const handleCopy = useCallback(() => {
    if (innerValue) {
      navigator.clipboard.writeText(innerValue);
    }
  }, [innerValue]);

  useEffect(() => {
    setInnerValue(value ?? defaultValue);
  }, [value, defaultValue]);

  const handleUpdate = useCallback(
    (val: string) => {
      setNativeValue(inputRef?.current, val);
      // trigger change event if value changed (not for focus events)
      inputRef?.current?.dispatchEvent(new Event('change', { bubbles: true }));
      onChange && onChange(val);
      setInnerValue(val);
    },
    [onChange],
  );

  const _completionsDepCheck = JSON.stringify(completions);

  const filterListByCompletionType = (
    completionsList: Completion[],
    filterType: string,
  ) => {
    return completionsList.filter(item => item.type === filterType);
  };

  const generateAutocompletionRules = useCallback(
    async (
      context: CompletionContext,
      completions: Completion[],
      regex: string,
      type: string,
    ) => {
      const filteredCompletions = filterListByCompletionType(completions, type);
      const completionsList: CompletionSource =
        completeFromList(filteredCompletions);
      const options = (await completionsList(context)) as CompletionResult;

      // if the user types the regex value, we display the completions list
      const hasTriggeredAutocomplete = context.matchBefore(new RegExp(regex));

      if (hasTriggeredAutocomplete) {
        return {
          from: context.pos,
          options: filterListByCompletionType(completions, type),
        };
      }

      // if the user type the regex value and "<string>"
      const doesValueMatch = context.matchBefore(new RegExp(`${regex}(.*?)`));

      // if no options or no match for the typed value
      if (!options?.options || !doesValueMatch) {
        return null;
      }

      return {
        ...options,
      };
    },
    // biome-ignore lint/correctness/useExhaustiveDependencies: existing
    [filterListByCompletionType],
  );

  const extensions = useMemo(() => {
    const extensions = [
      ...extraExtensions,
      autocompletion({
        // triggering auto complete when matching regex pattern
        override: [
          async context =>
            await generateAutocompletionRules(
              context,
              completions,
              'return (\'|")',
              'nodes',
            ),
          async context =>
            await generateAutocompletionRules(
              context,
              completions,
              'context.write_metric\\((\'|")',
              'metrics',
            ),
        ],
      }),
      keymap.of([...defaultKeymap, ...completionKeymap, indentWithTab]),
    ];

    if (diagnostics) {
      extensions.push(lintGutter());
    }

    if (language === 'python') {
      extensions.push(python());
    } else if (language === 'json') {
      extensions.push(json());
    } else if (language === 'yaml') {
      extensions.push(yaml());
    }
    if (skipLines !== undefined && showLineNumbers) {
      extensions.push(
        lineNumbers({
          formatNumber: lineNo => (lineNo + skipLines).toString(),
        }),
      );
    }
    return extensions;
  }, [
    diagnostics,
    extraExtensions,
    generateAutocompletionRules,
    language,
    skipLines,
    completions,
    showLineNumbers,
  ]);

  useEffect(() => {
    if (diagnostics && view.current) {
      view.current.view?.dispatch(
        setDiagnostics(view.current.view.state, diagnostics),
      );
    }
  }, [diagnostics, view]);

  return (
    <Styled.Wrapper
      className={cn('nokey', className, {
        lineWrapping,
        showCopyButton,
      })}
      data-test-id={dataTestId}
    >
      {showCopyButton && (
        <>
          <Styled.GutterExtension $showLintGutter={!!diagnostics} />
          <Styled.CopyBackground $showLintGutter={!!diagnostics} />
          <Styled.CopyButton>
            <Tooltip
              content="Copied to clipboard"
              data-test-id="copy-btn"
              trigger={['click']}
            >
              <IconButton onClick={handleCopy}>
                <CopyIcon color="iconSecondaryInverse" />
              </IconButton>
            </Tooltip>
          </Styled.CopyButton>
        </>
      )}
      <ReactCodeMirror
        theme={isDarkMode ? 'dark' : 'light'}
        ref={view}
        basicSetup={{ lineNumbers: showLineNumbers }}
        className={cn({ codeEditorWithError: errors?.length > 0 })}
        extensions={extensions}
        placeholder={placeholder}
        readOnly={readOnly}
        value={value ?? innerValue}
        onBlur={(e: any) => {
          // change event target to real input
          inputRef.current && (e.target = inputRef.current);
          onBlur && onBlur(e);
        }}
        onChange={handleUpdate}
      />
      {errors?.length > 0 && (
        <Styled.ErrorCaption>{errors[0]}</Styled.ErrorCaption>
      )}
      <KeyboardSafeTextArea
        ref={inputRef}
        defaultValue={defaultValue}
        name={name}
        style={{ display: 'none' }}
      />
    </Styled.Wrapper>
  );
};
